'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ConfidenceIntervalCalculator() {
  const t = useTranslations("tools.confidenceIntervalCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    sampleSize: 50,
    sampleMean: 20.6,
    stdDev: 3.2,
    confidenceLevel: 95
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  // Z-values for different confidence levels
  const zValues: { [key: number]: number } = {
    70: 1.036,
    75: 1.150,
    80: 1.282,
    85: 1.440,
    90: 1.645,
    95: 1.960,
    98: 2.326,
    99: 2.576,
    99.5: 2.807,
    99.9: 3.291
  };

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { sampleSize, sampleMean, stdDev, confidenceLevel } = inputs;

    if (sampleSize <= 0) {
      setError(t('errorSampleSize'));
      setResults(null);
      return;
    }

    if (stdDev < 0) {
      setError(t('errorStdDev'));
      setResults(null);
      return;
    }

    const zValue = zValues[confidenceLevel];
    const standardError = stdDev / Math.sqrt(sampleSize);
    const marginOfError = zValue * standardError;
    const lowerBound = sampleMean - marginOfError;
    const upperBound = sampleMean + marginOfError;

    setResults({
      lowerBound,
      upperBound,
      marginOfError,
      standardError,
      sampleMean,
      confidenceLevel
    });
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#f59e0b',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem' },
      { padding: '3rem 2rem' }
    ),
    toolContainer: getResponsiveStyle(
      { margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden' as const },
      { maxWidth: '100%' },
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '2rem',
      textAlign: 'center' as const
    },
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '1.8rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(2, 1fr)' }
    ),
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const
    },
    label: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    labelSub: {
      fontSize: '0.85rem',
      color: colors.textSecondary,
      fontWeight: 400,
      display: 'block',
      marginTop: '0.25rem'
    },
    input: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        background: 'white'
      },
      {},
      {},
      {}
    ),
    select: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        background: 'white',
        cursor: 'pointer'
      },
      {},
      {},
      {}
    ),
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' }
    ),
    errorMessage: {
      background: '#fee2e2',
      borderLeft: '4px solid #ef4444',
      padding: '1rem',
      borderRadius: '0.5rem',
      color: '#991b1b',
      marginTop: '1rem',
      fontSize: '0.95rem'
    },
    results: getResponsiveStyle(
      {
        padding: '2rem',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderTop: '3px solid #2563eb'
      },
      {},
      {},
      {}
    ),
    resultsTitle: getResponsiveStyle(
      { margin: '0 0 1.5rem 0', color: colors.textPrimary, textAlign: 'center' as const },
      { fontSize: '1.3rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultMain: getResponsiveStyle(
      {
        background: 'white',
        padding: '1.5rem',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
      },
      {},
      {},
      {}
    ),
    resultMainTitle: {
      fontSize: '0.9rem',
      color: colors.textSecondary,
      marginBottom: '0.5rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px'
    },
    resultMainValue: getResponsiveStyle(
      { fontWeight: 700, color: colors.primary, marginBottom: '0.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    resultMainRange: getResponsiveStyle(
      { color: colors.textSecondary, fontWeight: 500 },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.2rem' }
    ),
    resultGrid: getResponsiveStyle(
      { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(4, 1fr)' }
    ),
    resultItem: {
      background: 'white',
      padding: '1rem',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #7c3aed',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },
    resultLabel: {
      display: 'block',
      fontSize: '0.85rem',
      color: colors.textSecondary,
      marginBottom: '0.25rem',
      fontWeight: 500
    },
    resultValue: getResponsiveStyle(
      { display: 'block', fontWeight: 700, color: colors.textPrimary },
      { fontSize: '1.1rem' },
      { fontSize: '1.2rem' },
      { fontSize: '1.3rem' }
    ),
    visualDisplay: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      marginTop: '1.5rem',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
    },
    visualTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
      textAlign: 'center' as const
    },
    intervalBar: {
      position: 'relative' as const,
      height: '60px',
      margin: '2rem 0'
    },
    intervalLine: {
      position: 'absolute' as const,
      top: '50%',
      left: '10%',
      right: '10%',
      height: '4px',
      background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)',
      borderRadius: '2px',
      transform: 'translateY(-50%)'
    },
    intervalCap: {
      position: 'absolute' as const,
      top: '50%',
      width: '3px',
      height: '30px',
      background: colors.textPrimary,
      transform: 'translateY(-50%)'
    },
    intervalPoint: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      width: '16px',
      height: '16px',
      background: colors.accent,
      border: '3px solid white',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
    },
    intervalLabel: getResponsiveStyle(
      {
        position: 'absolute' as const,
        top: '100%',
        color: colors.textSecondary,
        fontWeight: 600,
        marginTop: '0.5rem',
        whiteSpace: 'nowrap' as const
      },
      { fontSize: '0.75rem' },
      { fontSize: '0.85rem' },
      { fontSize: '0.85rem' }
    ),
    interpretation: {
      background: '#fef3c7',
      borderLeft: '4px solid #f59e0b',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1rem'
    },
    interpretationText: {
      color: '#78350f',
      fontSize: '0.95rem',
      lineHeight: 1.6,
      margin: 0
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(37, 99, 235, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.4rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    strong: {
      fontWeight: 600,
      color: colors.textPrimary
    },
    insightCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)',
        border: '2px solid rgba(245, 158, 11, 0.3)',
        borderRadius: '0.75rem',
        marginBottom: '1rem'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqItem: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.5)',
        borderRadius: '0.75rem',
        marginBottom: '1rem',
        border: '1px solid rgba(37, 99, 235, 0.1)'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.primary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' }
    ),
    referenceList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    referenceItem: {
      marginBottom: '0.75rem',
      paddingLeft: '1.5rem',
      position: 'relative' as const
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontSize: '0.95rem',
      transition: 'color 0.3s ease',
      wordBreak: 'break-word' as const
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolHeaderTitle}>{t('toolTitle')}</h2>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label htmlFor="sample-size" style={styles.label}>
                  {t('sampleSizeLabel')}
                  <span style={styles.labelSub}>{t('sampleSizeSub')}</span>
                </label>
                <input
                  type="number"
                  id="sample-size"
                  min="1"
                  step="1"
                  value={inputs.sampleSize}
                  onChange={(e) => setInputs({...inputs, sampleSize: parseFloat(e.target.value)})}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="sample-mean" style={styles.label}>
                  {t('sampleMeanLabel')}
                  <span style={styles.labelSub}>{t('sampleMeanSub')}</span>
                </label>
                <input
                  type="number"
                  id="sample-mean"
                  step="any"
                  value={inputs.sampleMean}
                  onChange={(e) => setInputs({...inputs, sampleMean: parseFloat(e.target.value)})}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="std-dev" style={styles.label}>
                  {t('stdDevLabel')}
                  <span style={styles.labelSub}>{t('stdDevSub')}</span>
                </label>
                <input
                  type="number"
                  id="std-dev"
                  min="0"
                  step="any"
                  value={inputs.stdDev}
                  onChange={(e) => setInputs({...inputs, stdDev: parseFloat(e.target.value)})}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="confidence-level" style={styles.label}>
                  {t('confidenceLevelLabel')}
                  <span style={styles.labelSub}>{t('confidenceLevelSub')}</span>
                </label>
                <select
                  id="confidence-level"
                  value={inputs.confidenceLevel}
                  onChange={(e) => setInputs({...inputs, confidenceLevel: parseFloat(e.target.value)})}
                  style={styles.select}
                >
                  <option value="70">70%</option>
                  <option value="75">75%</option>
                  <option value="80">80%</option>
                  <option value="85">85%</option>
                  <option value="90">90%</option>
                  <option value="95">95%</option>
                  <option value="98">98%</option>
                  <option value="99">99%</option>
                  <option value="99.5">99.5%</option>
                  <option value="99.9">99.9%</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              style={styles.calculateButton}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {t('calculateButton')}
            </button>

            {error && (
              <div style={styles.errorMessage}>
                {error}
              </div>
            )}
          </form>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>

              <div style={styles.resultMain}>
                <div style={styles.resultMainTitle}>{t('confidenceIntervalLabel')}</div>
                <div style={styles.resultMainValue}>
                  {results.sampleMean.toFixed(4)} ± {results.marginOfError.toFixed(4)}
                </div>
                <div style={styles.resultMainRange}>
                  [{results.lowerBound.toFixed(4)} – {results.upperBound.toFixed(4)}]
                </div>
              </div>

              <div style={styles.resultGrid}>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('lowerBoundLabel')}</span>
                  <span style={styles.resultValue}>{results.lowerBound.toFixed(4)}</span>
                </div>

                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('upperBoundLabel')}</span>
                  <span style={styles.resultValue}>{results.upperBound.toFixed(4)}</span>
                </div>

                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('marginErrorLabel')}</span>
                  <span style={styles.resultValue}>±{results.marginOfError.toFixed(4)}</span>
                </div>

                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('stdErrorLabel')}</span>
                  <span style={styles.resultValue}>{results.standardError.toFixed(4)}</span>
                </div>
              </div>

              <div style={styles.visualDisplay}>
                <div style={styles.visualTitle}>{t('visualTitle')}</div>
                <div style={styles.intervalBar}>
                  <div style={styles.intervalLine}></div>
                  <div style={{...styles.intervalCap, left: '10%'}}></div>
                  <div style={{...styles.intervalCap, right: '10%'}}></div>
                  <div style={styles.intervalPoint}></div>
                  <div style={{...styles.intervalLabel, left: '10%', transform: 'translateX(-50%)'}}>
                    {results.lowerBound.toFixed(2)}
                  </div>
                  <div style={{...styles.intervalLabel, left: '50%', transform: 'translateX(-50%)', color: colors.accent}}>
                    {results.sampleMean.toFixed(2)}
                  </div>
                  <div style={{...styles.intervalLabel, right: '10%', transform: 'translateX(50%)'}}>
                    {results.upperBound.toFixed(2)}
                  </div>
                </div>
              </div>

              <div style={styles.interpretation}>
                <p style={styles.interpretationText}>
                  {t('interpretationText', {
                    confidenceLevel: results.confidenceLevel,
                    lowerBound: results.lowerBound.toFixed(4),
                    upperBound: results.upperBound.toFixed(4)
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Introduction */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('step4Title')}</strong> {t('step4Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('step5Title')}</strong> {t('step5Description')}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight1Title')}</h3>
          <p style={styles.paragraph}>{t('insight1Description')}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight2Title')}</h3>
          <p style={styles.paragraph}>{t('insight2Description')}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight3Title')}</h3>
          <p style={styles.paragraph}>{t('insight3Description')}</p>
        </div>

        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight4Title')}</h3>
          <p style={styles.paragraph}>{t('insight4Description')}</p>
        </div>
      </section>

      {/* Understanding the Formula */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('formulaTitle')}</h2>
        <p style={styles.paragraph}>{t('formulaIntro')}</p>

        <div style={{...styles.insightCard, background: 'rgba(37, 99, 235, 0.05)', border: '2px solid rgba(37, 99, 235, 0.2)'}}>
          <p style={{...styles.paragraph, fontFamily: 'monospace', fontSize: '1.1rem', textAlign: 'center', fontWeight: 600}}>
            {t('formulaEquation')}
          </p>
        </div>

        <h3 style={styles.h3}>{t('formulaComponentsTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t('component1Title')}</strong> {t('component1Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('component2Title')}</strong> {t('component2Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('component3Title')}</strong> {t('component3Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('component4Title')}</strong> {t('component4Description')}</li>
        </ul>
      </section>

      {/* Common Applications */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('applicationsTitle')}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong style={styles.strong}>{t('application1Title')}</strong> {t('application1Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('application2Title')}</strong> {t('application2Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('application3Title')}</strong> {t('application3Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('application4Title')}</strong> {t('application4Description')}</li>
          <li style={styles.listItem}><strong style={styles.strong}>{t('application5Title')}</strong> {t('application5Description')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq1Question')}</div>
          <div style={styles.faqAnswer}>{t('faq1Answer')}</div>
        </div>

        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq2Question')}</div>
          <div style={styles.faqAnswer}>{t('faq2Answer')}</div>
        </div>

        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq3Question')}</div>
          <div style={styles.faqAnswer}>{t('faq3Answer')}</div>
        </div>

        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq4Question')}</div>
          <div style={styles.faqAnswer}>{t('faq4Answer')}</div>
        </div>

        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq5Question')}</div>
          <div style={styles.faqAnswer}>{t('faq5Answer')}</div>
        </div>

        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq6Question')}</div>
          <div style={styles.faqAnswer}>{t('faq6Answer')}</div>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ul style={styles.referenceList}>
          <li style={styles.referenceItem}>
            <span style={{position: 'absolute', left: 0, color: colors.primary}}>1.</span>
            <a
              href="https://www.simplypsychology.org/confidence-interval.html"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <span style={{position: 'absolute', left: 0, color: colors.primary}}>2.</span>
            <a
              href="https://www.scribbr.com/statistics/confidence-interval/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <span style={{position: 'absolute', left: 0, color: colors.primary}}>3.</span>
            <a
              href="https://www.statisticshowto.com/probability-and-statistics/confidence-interval/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <span style={{position: 'absolute', left: 0, color: colors.primary}}>4.</span>
            <a
              href="https://en.wikipedia.org/wiki/Confidence_interval"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('reference4')}
            </a>
          </li>
          <li style={styles.referenceItem}>
            <span style={{position: 'absolute', left: 0, color: colors.primary}}>5.</span>
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC5723800/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('reference5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}